// C:\IntegraNube_WebStore\src\pages\FinalizarCompra.js
'use strict';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  ArrowBackOutlined as ArrowBackIcon,
  CheckCircleOutline as CheckIcon,
  LocalShippingOutlined as ShippingIcon,
  LockOutlined as LockIcon,
  RemoveShoppingCartOutlined as EmptyIcon,
  ShieldOutlined as ShieldIcon,
  StorefrontOutlined as StoreIcon,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';

const CART_STORAGE_KEY = 'integranube_webstore_cart';
const TURNSTILE_SCRIPT_ID = 'integranube-turnstile-script';
const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const CLP = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function toNum(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function cleanString(value) {
  const result = String(value ?? '').trim();
  return result || '';
}

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') {
    return {};
  }

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object'
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (
      source.data.branding ||
      source.data.nombre ||
      source.data.ecommerce
    )
  ) {
    return source.data;
  }

  return source;
}

function readCart() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw =
      window.localStorage.getItem(
        CART_STORAGE_KEY
      );

    const cart =
      raw
        ? JSON.parse(raw)
        : [];

    return Array.isArray(cart)
      ? cart
      : [];
  } catch (_) {
    return [];
  }
}

function quantityOf(item) {
  return Math.max(
    1,
    toNum(
      item?.cantidad ??
      item?.quantity,
      1
    )
  );
}

function currentPriceOf(item) {
  return Math.max(
    0,
    toNum(
      item?.precio ??
      item?.precio_bruto ??
      item?.precioNeto ??
      item?.precio_neto,
      0
    )
  );
}

function normalPriceOf(item) {
  const actual =
    currentPriceOf(item);

  const normal =
    toNum(
      item?.precioNormal ??
      item?.precio_normal ??
      item?.precioNormalDetalle?.bruto,
      0
    );

  return normal > actual
    ? normal
    : actual;
}

function getProductImage(item) {
  const candidates = [
    item?.imagen,
    item?.imagenUrl,
    item?.imagen_url,
    Array.isArray(item?.imagenes)
      ? item.imagenes[0]
      : null,
  ];

  for (const candidate of candidates) {
    if (
      typeof candidate === 'string' &&
      candidate.trim()
    ) {
      return candidate.trim();
    }

    if (
      candidate &&
      typeof candidate === 'object'
    ) {
      const url =
        candidate.url ||
        candidate.src ||
        candidate.imagen;

      if (
        typeof url === 'string' &&
        url.trim()
      ) {
        return url.trim();
      }
    }
  }

  return null;
}

function buildAddress(direccion = {}) {
  return [
    direccion?.direccion,
    direccion?.comuna,
    direccion?.ciudad,
    direccion?.region,
  ]
    .map((value) =>
      cleanString(value)
    )
    .filter(Boolean)
    .filter(
      (value, index, array) =>
        array.indexOf(value) === index
    )
    .join(', ');
}

function isValidEmail(value) {
  const email =
    cleanString(value);

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    email
  );
}

function loadTurnstileScript() {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return Promise.reject(
      new Error(
        'Turnstile requiere un navegador.'
      )
    );
  }

  if (window.turnstile) {
    return Promise.resolve(
      window.turnstile
    );
  }

  return new Promise(
    (resolve, reject) => {
      let script =
        document.getElementById(
          TURNSTILE_SCRIPT_ID
        );

      const handleLoad = () => {
        if (window.turnstile) {
          resolve(window.turnstile);
          return;
        }

        reject(
          new Error(
            'Turnstile cargó sin exponer su API.'
          )
        );
      };

      const handleError = () => {
        reject(
          new Error(
            'No fue posible cargar Cloudflare Turnstile.'
          )
        );
      };

      if (script) {
        script.addEventListener(
          'load',
          handleLoad,
          { once: true }
        );

        script.addEventListener(
          'error',
          handleError,
          { once: true }
        );

        return;
      }

      script =
        document.createElement('script');

      script.id =
        TURNSTILE_SCRIPT_ID;

      script.src =
        TURNSTILE_SCRIPT_URL;

      script.async = true;
      script.defer = true;

      script.addEventListener(
        'load',
        handleLoad,
        { once: true }
      );

      script.addEventListener(
        'error',
        handleError,
        { once: true }
      );

      document.head.appendChild(
        script
      );
    }
  );
}

function TurnstileBox({
  siteKey,
  colorPrimario,
  onToken,
  onError,
}) {
  const containerRef =
    useRef(null);

  const widgetIdRef =
    useRef(null);

  useEffect(() => {
    let mounted = true;

    if (!siteKey) {
      onToken('');
      return undefined;
    }

    loadTurnstileScript()
      .then((turnstile) => {
        if (
          !mounted ||
          !containerRef.current
        ) {
          return;
        }

        if (
          widgetIdRef.current !== null
        ) {
          try {
            turnstile.remove(
              widgetIdRef.current
            );
          } catch (_) {
            // No bloqueamos el checkout por una limpieza previa.
          }

          widgetIdRef.current =
            null;
        }

        widgetIdRef.current =
          turnstile.render(
            containerRef.current,
            {
              sitekey: siteKey,
              theme: 'auto',
              size: 'flexible',
              action: 'checkout',

              callback: (token) => {
                if (mounted) {
                  onToken(
                    cleanString(token)
                  );
                }
              },

              'expired-callback': () => {
                if (mounted) {
                  onToken('');

                  try {
                    if (
                      widgetIdRef.current !== null
                    ) {
                      turnstile.reset(
                        widgetIdRef.current
                      );
                    }
                  } catch (_) {
                    // El usuario podrá recargar la vista si el reset falla.
                  }
                }
              },

              'timeout-callback': () => {
                if (mounted) {
                  onToken('');

                  try {
                    if (
                      widgetIdRef.current !== null
                    ) {
                      turnstile.reset(
                        widgetIdRef.current
                      );
                    }
                  } catch (_) {
                    // Limpieza defensiva.
                  }
                }
              },

              'error-callback': (
                errorCode
              ) => {
                if (mounted) {
                  onToken('');
                  onError(
                    errorCode
                      ? `Turnstile no pudo completar la verificación (${errorCode}).`
                      : 'Turnstile no pudo completar la verificación.'
                  );
                }
              },
            }
          );
      })
      .catch((error) => {
        if (mounted) {
          onToken('');
          onError(
            error?.message ||
            'No fue posible cargar Turnstile.'
          );
        }
      });

    return () => {
      mounted = false;

      if (
        window.turnstile &&
        widgetIdRef.current !== null
      ) {
        try {
          window.turnstile.remove(
            widgetIdRef.current
          );
        } catch (_) {
          // Limpieza defensiva.
        }
      }

      widgetIdRef.current = null;
    };
  }, [
    siteKey,
    onToken,
    onError,
  ]);

  if (!siteKey) {
    return (
      <Alert
        severity="warning"
        sx={{
          borderRadius: 2,
        }}
      >
        Cloudflare Turnstile todavía no está configurado.
        Define <strong>REACT_APP_TURNSTILE_SITE_KEY</strong>
        {' '}antes de habilitar pedidos reales.
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          minHeight: 65,

          '& iframe': {
            maxWidth: '100%',
          },
        }}
      />

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
          mt: 1,
          color: '#64748b',
        }}
      >
        <ShieldIcon
          sx={{
            fontSize: 17,
            color: colorPrimario,
          }}
        />

        <Typography
          variant="caption"
          sx={{
            lineHeight: 1.4,
          }}
        >
          Verificación de seguridad protegida por Cloudflare.
        </Typography>
      </Stack>
    </Box>
  );
}

export default function FinalizarCompra({
  tienda = null,
}) {
  const navigate =
    useNavigate();

  const data =
    useMemo(
      () =>
        resolveTiendaData(tienda),
      [tienda]
    );

  const colorPrimario =
    cleanString(
      data?.branding?.colorPrimario
    ) ||
    '#1976d2';

  const retiroDisponible =
    data?.ecommerce
      ?.retiroDisponible !== false;

  const despachoDisponible =
    data?.ecommerce
      ?.despachoDisponible === true;

  const checkoutHabilitado =
    data?.funciones
      ?.checkout !== false;

  const direccionTienda =
    buildAddress(
      data?.direccion
    );

  const turnstileSiteKey =
    cleanString(
      data?.seguridad
        ?.turnstileSiteKey
    ) ||
    cleanString(
      process.env
        .REACT_APP_TURNSTILE_SITE_KEY
    );

  const initialEntrega =
    retiroDisponible
      ? 'retiro'
      : despachoDisponible
        ? 'despacho'
        : '';

  const [
    cart,
    setCart,
  ] = useState([]);

  const [
    entrega,
    setEntrega,
  ] = useState(
    initialEntrega
  );

  const [
    turnstileToken,
    setTurnstileToken,
  ] = useState('');

  const [
    turnstileError,
    setTurnstileError,
  ] = useState('');

  const [
    snack,
    setSnack,
  ] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const [
    form,
    setForm,
  ] = useState({
    nombre: '',
    email: '',
    telefono: '',
    rut: '',
    direccion: '',
    comuna: '',
    ciudad: '',
    observaciones: '',
  });

  useEffect(() => {
    setCart(
      readCart()
    );
  }, []);

  useEffect(() => {
    if (!entrega) {
      if (retiroDisponible) {
        setEntrega('retiro');
      } else if (
        despachoDisponible
      ) {
        setEntrega('despacho');
      }
    }
  }, [
    entrega,
    retiroDisponible,
    despachoDisponible,
  ]);

  const handleTurnstileToken =
    useCallback((token) => {
      setTurnstileToken(token);
      setTurnstileError('');
    }, []);

  const handleTurnstileError =
    useCallback((message) => {
      setTurnstileError(
        cleanString(message) ||
        'No fue posible completar la verificación de seguridad.'
      );
    }, []);

  const resumen =
    useMemo(() => {
      let unidades = 0;
      let total = 0;
      let totalNormal = 0;

      cart.forEach((item) => {
        const cantidad =
          quantityOf(item);

        const precio =
          currentPriceOf(item);

        const precioNormal =
          normalPriceOf(item);

        unidades += cantidad;
        total +=
          precio * cantidad;

        totalNormal +=
          precioNormal * cantidad;
      });

      const ahorro =
        Math.max(
          0,
          totalNormal - total
        );

      return {
        unidades,
        total,
        totalNormal,
        ahorro,
        tieneAhorro:
          ahorro > 0,
      };
    }, [cart]);

  const updateField =
    (field, value) => {
      setForm(
        (prev) => ({
          ...prev,
          [field]: value,
        })
      );
    };

  const showSnack = (
    message,
    severity = 'info'
  ) => {
    setSnack({
      open: true,
      message,
      severity,
    });
  };

  const handleContinue = () => {
    if (!checkoutHabilitado) {
      showSnack(
        'La compra online no está habilitada en esta tienda.',
        'warning'
      );
      return;
    }

    if (
      !cleanString(form.nombre) ||
      !cleanString(form.email) ||
      !cleanString(form.telefono)
    ) {
      showSnack(
        'Completa tu nombre, email y teléfono.',
        'warning'
      );
      return;
    }

    if (
      !isValidEmail(form.email)
    ) {
      showSnack(
        'Ingresa un email válido.',
        'warning'
      );
      return;
    }

    if (!entrega) {
      showSnack(
        'Selecciona una modalidad de entrega.',
        'warning'
      );
      return;
    }

    if (
      entrega === 'despacho' &&
      (
        !cleanString(
          form.direccion
        ) ||
        !cleanString(
          form.comuna
        )
      )
    ) {
      showSnack(
        'Completa dirección y comuna para el despacho.',
        'warning'
      );
      return;
    }

    if (!turnstileSiteKey) {
      showSnack(
        'Turnstile aún no está configurado para esta tienda.',
        'warning'
      );
      return;
    }

    if (!turnstileToken) {
      showSnack(
        'Completa la verificación de seguridad antes de continuar.',
        'warning'
      );
      return;
    }

    /*
     * Este objeto queda listo para enviarlo al endpoint real de creación
     * de pedido.
     *
     * IMPORTANTE:
     * turnstileToken debe ser validado por el backend contra Cloudflare
     * Siteverify ANTES de crear/reservar/pagar el pedido.
     *
     * Nunca colocar TURNSTILE_SECRET_KEY en React.
     */
    const pedidoPayload = {
      cliente: {
        nombre:
          cleanString(
            form.nombre
          ),

        email:
          cleanString(
            form.email
          ),

        telefono:
          cleanString(
            form.telefono
          ),

        rut:
          cleanString(
            form.rut
          ) ||
          null,
      },

      entrega: {
        tipo: entrega,

        direccion:
          entrega === 'despacho'
            ? cleanString(
                form.direccion
              )
            : null,

        comuna:
          entrega === 'despacho'
            ? cleanString(
                form.comuna
              )
            : null,

        ciudad:
          entrega === 'despacho'
            ? cleanString(
                form.ciudad
              ) ||
              null
            : null,
      },

      observaciones:
        cleanString(
          form.observaciones
        ) ||
        null,

      turnstileToken,

      /*
       * Los precios y totales reales siempre deben recalcularse
       * en el backend usando los IDs/SKU/cantidades.
       * Estos valores del navegador son solamente visuales.
       */
      carrito: cart.map(
        (item) => ({
          id: item.id,
          sku:
            item.sku ||
            null,
          cantidad:
            quantityOf(item),
        })
      ),
    };

    console.info(
      '[FinalizarCompra] Payload preparado:',
      pedidoPayload
    );

    showSnack(
      'Datos y verificación listos. El siguiente paso es conectar la creación del pedido y el medio de pago.',
      'success'
    );
  };

  if (!checkoutHabilitado) {
    return (
      <Container
        maxWidth="md"
        sx={{
          py: 6,
        }}
      >
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              onClick={() =>
                navigate(
                  '/productos'
                )
              }
            >
              Ver productos
            </Button>
          }
        >
          La compra online no está habilitada en esta tienda.
        </Alert>
      </Container>
    );
  }

  if (!cart.length) {
    return (
      <Container
        maxWidth="md"
        sx={{
          py: 6,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: {
              xs: 3,
              md: 5,
            },
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <EmptyIcon
            sx={{
              fontSize: 64,
              color: '#cbd5e1',
              mb: 1.5,
            }}
          />

          <Typography
            variant="h5"
            fontWeight={900}
          >
            Tu carrito está vacío
          </Typography>

          <Typography
            color="text.secondary"
            sx={{
              mt: 1,
              mb: 2.5,
            }}
          >
            Agrega productos antes de finalizar tu compra.
          </Typography>

          <Button
            variant="contained"
            onClick={() =>
              navigate(
                '/productos'
              )
            }
            sx={{
              bgcolor:
                colorPrimario,
              borderRadius: 2,
              px: 3,
              textTransform:
                'none',
              fontWeight: 900,
              boxShadow: 'none',

              '&:hover': {
                bgcolor:
                  colorPrimario,
                filter:
                  'brightness(0.92)',
                boxShadow: 'none',
              },
            }}
          >
            Ver productos
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#f8fafc',
        minHeight:
          'calc(100vh - 80px)',
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          py: {
            xs: 2.5,
            md: 4,
          },
        }}
      >
        <Stack
          spacing={2.5}
        >
          <Button
            startIcon={
              <ArrowBackIcon />
            }
            onClick={() =>
              navigate('/carrito')
            }
            sx={{
              alignSelf:
                'flex-start',
              color: '#475569',
              borderRadius: 2,
              textTransform:
                'none',
              fontWeight: 800,
            }}
          >
            Volver al carrito
          </Button>

          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              md: 'center',
            }}
          >
            <Box>
              <Typography
                component="h1"
                sx={{
                  color: '#0f172a',
                  fontSize: {
                    xs: 30,
                    md: 38,
                  },
                  lineHeight: 1.1,
                  letterSpacing:
                    '-0.04em',
                  fontWeight: 950,
                }}
              >
                Finaliza tu compra
              </Typography>

              <Typography
                color="text.secondary"
                sx={{
                  mt: 0.75,
                }}
              >
                Completa tus datos y revisa tu pedido antes de continuar.
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Chip
                icon={
                  <LockIcon
                    sx={{
                      fontSize:
                        '17px !important',
                    }}
                  />
                }
                label="Compra protegida"
                variant="outlined"
                sx={{
                  borderColor:
                    '#dbe4ee',
                  bgcolor: '#fff',
                  fontWeight: 800,
                }}
              />

              <Chip
                label={`${resumen.unidades.toLocaleString(
                  'es-CL'
                )} ${
                  resumen.unidades === 1
                    ? 'producto'
                    : 'productos'
                }`}
                sx={{
                  bgcolor:
                    `${colorPrimario}12`,
                  color:
                    colorPrimario,
                  fontWeight: 800,
                }}
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg:
                  'minmax(0, 1fr) 390px',
              },
              gap: {
                xs: 2,
                lg: 2.5,
              },
              alignItems:
                'start',
            }}
          >
            <Stack
              spacing={2}
            >
              <SectionCard
                number="1"
                title="Tus datos"
                subtitle="Los usaremos para identificar tu pedido y mantenerte informado."
                colorPrimario={
                  colorPrimario
                }
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr 1fr',
                    },
                    gap: 1.5,
                  }}
                >
                  <TextField
                    fullWidth
                    required
                    label="Nombre"
                    autoComplete="name"
                    value={
                      form.nombre
                    }
                    onChange={(e) =>
                      updateField(
                        'nombre',
                        e.target.value
                      )
                    }
                  />

                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email"
                    autoComplete="email"
                    value={
                      form.email
                    }
                    onChange={(e) =>
                      updateField(
                        'email',
                        e.target.value
                      )
                    }
                  />

                  <TextField
                    fullWidth
                    required
                    label="Teléfono"
                    autoComplete="tel"
                    value={
                      form.telefono
                    }
                    onChange={(e) =>
                      updateField(
                        'telefono',
                        e.target.value
                      )
                    }
                  />

                  <TextField
                    fullWidth
                    label="RUT (opcional)"
                    value={
                      form.rut
                    }
                    onChange={(e) =>
                      updateField(
                        'rut',
                        e.target.value
                      )
                    }
                  />
                </Box>
              </SectionCard>

              <SectionCard
                number="2"
                title="¿Cómo quieres recibir tu compra?"
                subtitle="Elige una de las opciones disponibles para esta tienda."
                colorPrimario={
                  colorPrimario
                }
              >
                {!retiroDisponible &&
                !despachoDisponible ? (
                  <Alert
                    severity="warning"
                  >
                    Esta tienda no tiene modalidades de entrega habilitadas.
                  </Alert>
                ) : (
                  <Stack
                    spacing={1.25}
                  >
                    {retiroDisponible ? (
                      <DeliveryOption
                        selected={
                          entrega ===
                          'retiro'
                        }
                        icon={
                          <StoreIcon />
                        }
                        title="Retiro"
                        description={
                          direccionTienda
                            ? `Retira en ${direccionTienda}`
                            : 'Retira tu pedido directamente en la tienda.'
                        }
                        colorPrimario={
                          colorPrimario
                        }
                        onClick={() =>
                          setEntrega(
                            'retiro'
                          )
                        }
                      />
                    ) : null}

                    {despachoDisponible ? (
                      <DeliveryOption
                        selected={
                          entrega ===
                          'despacho'
                        }
                        icon={
                          <ShippingIcon />
                        }
                        title="Despacho"
                        description="Recibe tu compra en la dirección que indiques."
                        colorPrimario={
                          colorPrimario
                        }
                        onClick={() =>
                          setEntrega(
                            'despacho'
                          )
                        }
                      />
                    ) : null}
                  </Stack>
                )}

                {entrega ===
                'despacho' ? (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop:
                        '1px solid #eef2f7',
                    }}
                  >
                    <Typography
                      fontWeight={900}
                      sx={{
                        mb: 1.5,
                        color: '#334155',
                      }}
                    >
                      Dirección de entrega
                    </Typography>

                    <Stack
                      spacing={1.5}
                    >
                      <TextField
                        fullWidth
                        required
                        label="Dirección"
                        autoComplete="street-address"
                        value={
                          form.direccion
                        }
                        onChange={(e) =>
                          updateField(
                            'direccion',
                            e.target.value
                          )
                        }
                      />

                      <Box
                        sx={{
                          display:
                            'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm:
                              '1fr 1fr',
                          },
                          gap: 1.5,
                        }}
                      >
                        <TextField
                          fullWidth
                          required
                          label="Comuna"
                          value={
                            form.comuna
                          }
                          onChange={(e) =>
                            updateField(
                              'comuna',
                              e.target.value
                            )
                          }
                        />

                        <TextField
                          fullWidth
                          label="Ciudad"
                          autoComplete="address-level2"
                          value={
                            form.ciudad
                          }
                          onChange={(e) =>
                            updateField(
                              'ciudad',
                              e.target.value
                            )
                          }
                        />
                      </Box>
                    </Stack>
                  </Box>
                ) : null}
              </SectionCard>

              <SectionCard
                number="3"
                title="Indicaciones para tu pedido"
                subtitle="Opcional. Puedes agregar información útil para preparar o entregar tu compra."
                colorPrimario={
                  colorPrimario
                }
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={
                    form.observaciones
                  }
                  onChange={(e) =>
                    updateField(
                      'observaciones',
                      e.target.value
                    )
                  }
                  placeholder="Ej.: llamar al llegar, indicaciones de acceso, referencias..."
                />
              </SectionCard>

              <SectionCard
                number="4"
                title="Verificación de seguridad"
                subtitle="Antes de continuar verificamos que la solicitud sea legítima."
                colorPrimario={
                  colorPrimario
                }
              >
                <TurnstileBox
                  siteKey={
                    turnstileSiteKey
                  }
                  colorPrimario={
                    colorPrimario
                  }
                  onToken={
                    handleTurnstileToken
                  }
                  onError={
                    handleTurnstileError
                  }
                />

                {turnstileError ? (
                  <Alert
                    severity="warning"
                    sx={{
                      mt: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    {turnstileError}
                  </Alert>
                ) : null}

                {turnstileToken ? (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{
                      mt: 1.25,
                      color:
                        '#059669',
                    }}
                  >
                    <CheckIcon
                      sx={{
                        fontSize: 18,
                      }}
                    />

                    <Typography
                      variant="body2"
                      fontWeight={800}
                    >
                      Verificación completada
                    </Typography>
                  </Stack>
                ) : null}
              </SectionCard>
            </Stack>

            <OrderSummary
              cart={cart}
              resumen={resumen}
              colorPrimario={
                colorPrimario
              }
              canContinue={
                Boolean(
                  turnstileSiteKey &&
                  turnstileToken
                )
              }
              onContinue={
                handleContinue
              }
            />
          </Box>
        </Stack>
      </Container>

      <SnackbarAlert
        snack={snack}
        setSnack={setSnack}
      />
    </Box>
  );
}

function SectionCard({
  number,
  title,
  subtitle,
  colorPrimario,
  children,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: {
          xs: 2,
          sm: 2.5,
          md: 3,
        },
        border:
          '1px solid #e5e7eb',
        borderRadius: 3,
        bgcolor: '#fff',
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        sx={{
          mb: 2.25,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 999,
            display: 'grid',
            placeItems:
              'center',
            bgcolor:
              `${colorPrimario}12`,
            color:
              colorPrimario,
            fontWeight: 950,
            flexShrink: 0,
          }}
        >
          {number}
        </Box>

        <Box>
          <Typography
            variant="h6"
            fontWeight={950}
            sx={{
              color: '#0f172a',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {subtitle ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.45,
                lineHeight: 1.55,
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>

      {children}
    </Paper>
  );
}

function DeliveryOption({
  selected,
  icon,
  title,
  description,
  colorPrimario,
  onClick,
}) {
  return (
    <Paper
      component="button"
      type="button"
      elevation={0}
      onClick={onClick}
      sx={{
        width: '100%',
        p: 1.75,
        textAlign: 'left',
        cursor: 'pointer',
        borderRadius: 2.5,
        border:
          selected
            ? `2px solid ${colorPrimario}`
            : '1px solid #e2e8f0',
        bgcolor:
          selected
            ? `${colorPrimario}08`
            : '#fff',
        font: 'inherit',
        transition:
          'border-color 140ms ease, background-color 140ms ease',

        '&:hover': {
          borderColor:
            selected
              ? colorPrimario
              : '#94a3b8',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: 'grid',
            placeItems:
              'center',
            bgcolor:
              selected
                ? `${colorPrimario}14`
                : '#f8fafc',
            color:
              selected
                ? colorPrimario
                : '#64748b',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            fontWeight={900}
            sx={{
              color: '#0f172a',
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.25,
              color: '#64748b',
              lineHeight: 1.45,
            }}
          >
            {description}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: 999,
            border:
              selected
                ? `6px solid ${colorPrimario}`
                : '2px solid #cbd5e1',
            bgcolor: '#fff',
            flexShrink: 0,
          }}
        />
      </Stack>
    </Paper>
  );
}

function OrderSummary({
  cart,
  resumen,
  colorPrimario,
  canContinue,
  onContinue,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: {
          xs: 2,
          md: 2.5,
        },
        border:
          '1px solid #e5e7eb',
        borderRadius: 3,
        bgcolor: '#fff',
        alignSelf: 'start',
        position: {
          lg: 'sticky',
        },
        top: {
          lg: 18,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          variant="h6"
          fontWeight={950}
          sx={{
            color: '#0f172a',
          }}
        >
          Resumen del pedido
        </Typography>

        <Chip
          label={
            resumen.unidades
          }
          size="small"
          sx={{
            bgcolor:
              '#f1f5f9',
            fontWeight: 900,
          }}
        />
      </Stack>

      <Stack
        spacing={1.25}
        sx={{
          mt: 2,
          maxHeight: {
            lg: 360,
          },
          overflowY: {
            lg: 'auto',
          },
          pr: {
            lg: 0.5,
          },
        }}
      >
        {cart.map(
          (item) => (
            <OrderLine
              key={
                item.id ??
                item.sku
              }
              item={item}
              colorPrimario={
                colorPrimario
              }
            />
          )
        )}
      </Stack>

      <Divider
        sx={{
          my: 2,
        }}
      />

      <Stack
        spacing={1}
      >
        {resumen.tieneAhorro ? (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Precio normal
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textDecoration:
                    'line-through',
                }}
              >
                {CLP.format(
                  resumen.totalNormal
                )}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography
                variant="body2"
                sx={{
                  color:
                    '#059669',
                  fontWeight: 800,
                }}
              >
                Ahorras en ofertas
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color:
                    '#059669',
                  fontWeight: 900,
                }}
              >
                -{CLP.format(
                  resumen.ahorro
                )}
              </Typography>
            </Stack>
          </>
        ) : null}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          spacing={1}
          sx={{
            pt: 0.5,
          }}
        >
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Total
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              Impuestos incluidos
            </Typography>
          </Box>

          <Typography
            sx={{
              color: '#0f172a',
              fontSize: 27,
              lineHeight: 1,
              fontWeight: 950,
              letterSpacing:
                '-0.03em',
            }}
          >
            {CLP.format(
              resumen.total
            )}
          </Typography>
        </Stack>
      </Stack>

      <Button
        fullWidth
        variant="contained"
        size="large"
        startIcon={
          <LockIcon />
        }
        onClick={
          onContinue
        }
        disabled={
          !canContinue
        }
        sx={{
          mt: 2.25,
          minHeight: 52,
          borderRadius: 2,
          bgcolor:
            colorPrimario,
          textTransform:
            'none',
          fontWeight: 950,
          boxShadow: 'none',

          '&:hover': {
            bgcolor:
              colorPrimario,
            filter:
              'brightness(0.92)',
            boxShadow: 'none',
          },
        }}
      >
        Continuar con la compra
      </Button>

      {!canContinue ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            textAlign:
              'center',
            color: '#94a3b8',
            lineHeight: 1.45,
          }}
        >
          Completa la verificación de seguridad para continuar.
        </Typography>
      ) : null}

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        justifyContent="center"
        sx={{
          mt: 1.5,
          color: '#64748b',
        }}
      >
        <ShieldIcon
          sx={{
            fontSize: 17,
          }}
        />

        <Typography
          variant="caption"
          fontWeight={700}
        >
          Tus datos se envían de forma segura
        </Typography>
      </Stack>
    </Paper>
  );
}

function OrderLine({
  item,
  colorPrimario,
}) {
  const cantidad =
    quantityOf(item);

  const precio =
    currentPriceOf(item);

  const normal =
    normalPriceOf(item);

  const subtotal =
    precio * cantidad;

  const tieneOferta =
    normal > precio;

  const imagen =
    getProductImage(item);

  const descuento =
    tieneOferta &&
    normal > 0
      ? Math.round(
          (
            (normal - precio) /
            normal
          ) * 100
        )
      : 0;

  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
    >
      <Box
        sx={{
          width: 58,
          height: 58,
          borderRadius: 2,
          border:
            '1px solid #eef2f7',
          bgcolor: '#f8fafc',
          overflow: 'hidden',
          display: 'grid',
          placeItems:
            'center',
          flexShrink: 0,
          position:
            'relative',
        }}
      >
        {imagen ? (
          <Box
            component="img"
            src={imagen}
            alt={
              item.nombre ||
              'Producto'
            }
            loading="lazy"
            sx={{
              width: '100%',
              height: '100%',
              objectFit:
                'contain',
              p: 0.5,
            }}
          />
        ) : (
          <StoreIcon
            sx={{
              color: '#cbd5e1',
              fontSize: 28,
            }}
          />
        )}

        <Box
          sx={{
            position:
              'absolute',
            top: 2,
            right: 2,
            minWidth: 19,
            height: 19,
            px: 0.5,
            borderRadius: 999,
            display: 'grid',
            placeItems:
              'center',
            bgcolor:
              '#0f172a',
            color: '#fff',
            fontSize: 10,
            fontWeight: 900,
          }}
        >
          {cantidad}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          variant="body2"
          fontWeight={850}
          sx={{
            color: '#334155',
            lineHeight: 1.35,
            display:
              '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient:
              'vertical',
            overflow: 'hidden',
          }}
        >
          {item.nombre ||
            'Producto'}
        </Typography>

        {tieneOferta ? (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              mt: 0.35,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color:
                  '#94a3b8',
                textDecoration:
                  'line-through',
              }}
            >
              {CLP.format(
                normal
              )}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color:
                  '#059669',
                fontWeight: 900,
              }}
            >
              {descuento}% OFF
            </Typography>
          </Stack>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {CLP.format(
              precio
            )}{' '}
            c/u
          </Typography>
        )}
      </Box>

      <Typography
        variant="body2"
        fontWeight={900}
        sx={{
          color:
            tieneOferta
              ? colorPrimario
              : '#0f172a',
          whiteSpace:
            'nowrap',
        }}
      >
        {CLP.format(
          subtotal
        )}
      </Typography>
    </Stack>
  );
}

function SnackbarAlert({
  snack,
  setSnack,
}) {
  return (
    <Box>
      {/* Se mantiene fuera de la jerarquía visual principal. */}
      {snack.open ? (
        <Alert
          severity={
            snack.severity
          }
          variant="filled"
          onClose={() =>
            setSnack(
              (prev) => ({
                ...prev,
                open: false,
              })
            )
          }
          sx={{
            position: 'fixed',
            left: '50%',
            bottom: 24,
            transform:
              'translateX(-50%)',
            zIndex: 1600,
            width:
              'min(92vw, 620px)',
            boxShadow:
              '0 16px 40px rgba(15,23,42,0.22)',
          }}
        >
          {snack.message}
        </Alert>
      ) : null}
    </Box>
  );
}
